import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";

export interface FindingAction {
  text: string;
  assignee: string;
  dueDate: string;
  status: "overdue" | "on-time" | "closed";
}

export interface FindingDetailsModalData {
  findingId: string;
  date: string;
  assignee: string;
  location: string;
  description: string;
  level: string;       // e.g. "Level 1" — extracted from finding_type
  findingType: string; // audit_type e.g. "PSM"
  pictures: string[];
  avatars: string[];
  actions: FindingAction[];
}

@Injectable({ providedIn: "root" })
export class FindingDetailsModalService {
  isOpen$    = new BehaviorSubject<boolean>(false);
  modalData$ = new BehaviorSubject<FindingDetailsModalData | null>(null);

  setData(data: FindingDetailsModalData): void {
    this.modalData$.next(data);
  }

  /** Transform a raw creTableData row and open the modal. */
  openWithCreRow(row: any): void {
    this.setData(FindingDetailsModalService.fromCreRow(row));
    this.open();
  }

  open(): void {
    this.isOpen$.next(true);
    const ionContent = document.querySelector("ion-content.dashboard-wrapper") as HTMLElement;
    if (ionContent) ionContent.style.setProperty("--overflow", "hidden");
  }

  close(): void {
    this.isOpen$.next(false);
    const ionContent = document.querySelector("ion-content.dashboard-wrapper") as HTMLElement;
    if (ionContent) ionContent.style.removeProperty("--overflow");
  }

  static fromCreRow(row: any): FindingDetailsModalData {
    const today = new Date();
    return {
      findingId:   row.finding_number ?? "",
      date:        FindingDetailsModalService.formatDate(row.findings_due_date),
      assignee:    `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim(),
      location:    row.location ?? "",
      description: row.finding_details ?? "",
      level:       (row.finding_type ?? "").split("(")[0].trim(),
      findingType: row.audit_type ?? "",
      pictures:    [],
      avatars:     [],
      actions: (row.actions ?? []).map((a: any) => ({
        text:     a.action_description ?? "",
        assignee: `${a.action_assigned_to_first_name ?? ""} ${a.action_assigned_to_last_name ?? ""}`.trim(),
        dueDate:  FindingDetailsModalService.formatDate(a.due_date),
        status:   FindingDetailsModalService.computeStatus(a.sn_status, a.due_date, today),
      })),
    };
  }

  private static formatDate(dateStr: string): string {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return isNaN(d.getTime())
      ? dateStr
      : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  }

  private static computeStatus(
    snStatus: string,
    dueDateStr: string,
    today: Date
  ): "closed" | "overdue" | "on-time" {
    if ((snStatus ?? "").toLowerCase() === "closed") return "closed";
    if (dueDateStr && new Date(dueDateStr) < today) return "overdue";
    return "on-time";
  }
}
